
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	IconButton,
	InputAdornment,
	Stack,
	Box,
	Tooltip,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useDebounce } from "../../../../shared/hooks";
import { useInstallments } from "../../hooks/useInstallments";
import { useInstallmentModalReducer } from "../../hooks/useInstallmentModalReducer";
import { ModalConfirmSelect } from "./ModalConfirmSelect";
import { IGenerateInvoice, useGenerateInvoice } from "../../hooks/useGenerateInvoice";

import {
	globalFormatCurrencyBRL,
	globalMaskDocument,
	globalTranslatePayment,
	globalTranslateMethodPayment,
	globalTranslateStatusInvoices,
	globalBackgroundPayment,
	globalColorText,
} from "../../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { getModalTexts, ModalType } from "../../types/modalTexts";
import { useInstallmentHandlers } from "../../hooks/useInstallmentHandlers";
import { generateReferenceFromPeriod, getDueDateFromDay } from "../../utils/due_date_utils";

interface Props {
	open: boolean;
	onClose: () => void;
	dataIni: string;
	dataFim: string;
	row: { clientId: string; name: string; day_due_date: string };
	modalType: ModalType;
	filterByPaymentMethod: "credit_card" | "billet";
	onSuccess?: () => void;
}

export const ModalSelectInstallments = ({ open, onClose, dataIni, dataFim, row, modalType, filterByPaymentMethod, onSuccess }: Props) => {
	const { rows, isLoading } = useInstallments(open, row.clientId, dataIni, dataFim, filterByPaymentMethod);
	const { state, dispatch } = useInstallmentModalReducer();
	const { selectedIds, openConfirmModal } = state;
	const [searchText, setSearchText] = useState("");
	const [debouncedSearchText, setDebouncedSearchText] = useState("");
	const [obs, setObs] = useState("");
	const [loadingAction, setLoadingAction] = useState(false);
	const { debounce } = useDebounce();
	const [dueDate, setDueDate] = useState<string>("");

	const { handleCancelInstallments, handlePaymentInstallments, handleSwitchInstallments } = useInstallmentHandlers(dataIni, dataFim, setLoadingAction);
	const { handleGenerateInvoice } = useGenerateInvoice(dataIni, dataFim, setLoadingAction, onSuccess ?? (() => { }));

	useEffect(() => {
		debounce(() => setDebouncedSearchText(searchText));
	}, [searchText]);

	const normalize = (text: string) => text.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

	const sortedRows = useMemo(() => {
		return [...rows].sort((a, b) => a.name.localeCompare(b.name));
	}, [rows]);

	const filteredRows = sortedRows.filter((item) => normalize(`${item.name} ${item.document}`).includes(normalize(debouncedSearchText)));

	const allIds = filteredRows.filter((r) => r.Installment_status === "pending").map((r) => r.Installment_id);

	const isAllSelected = selectedIds.length === allIds.length && allIds.length > 0;
	const isIndeterminate = selectedIds.length > 0 && selectedIds.length < allIds.length;

	const toggleSelectAll = () => dispatch({ type: "TOGGLE_SELECT_ALL", payload: allIds });
	const toggleSelectOne = (id: string) => dispatch({ type: "TOGGLE_SELECT_ONE", payload: id });

	const { title, confirmTitle, confirmDescription, confirmLabel, cancelLabel } = getModalTexts(modalType, row.name);

	useEffect(() => {
		if (modalType === "generate" && selectedIds.length > 0 && !dueDate) {
			const defaultDueDate = getDueDateFromDay(Number(row.day_due_date || 0));
			setDueDate(defaultDueDate);
		}
	}, [modalType, selectedIds, dueDate]);

	const renderCustomContent = () => {
		if (modalType === "cancel" || modalType === "manual") {
			return (
				<TextField
					label={modalType === "cancel" ? "Motivo do cancelamento" : "Motivo da baixa manual"}
					value={obs}
					onChange={(e) => setObs(e.target.value)}
					fullWidth
					multiline
					rows={3}
				/>
			);
		}

		if (modalType === "convert") {
			const newStatusLabel = filterByPaymentMethod === "credit_card" ? "boleto bancário" : "cartão de crédito";
			return (
				<Typography variant="body2">
					As cobranças selecionadas serão convertidas para <strong>{newStatusLabel}</strong>.
				</Typography>
			);
		}

		if (modalType === "generate") {
			return (
				<TextField
					label="Data de vencimento"
					type="date"
					value={dueDate}
					onChange={(e) => setDueDate(e.target.value)}
					fullWidth
					InputLabelProps={{ shrink: true }}
				/>
			);
		}

		return null;
	};

	const handleConfirmAction = async () => {
		let success = false;

		if (modalType === "cancel") {
			await handleCancelInstallments(obs, selectedIds);
			success = true;
		} else if (modalType === "manual") {
			await handlePaymentInstallments(obs, selectedIds);
			success = true;
		} else if (modalType === "convert") {
			const targetStatus = filterByPaymentMethod === "credit_card" ? "billet" : "credit_card";
			await handleSwitchInstallments(selectedIds, targetStatus);
			success = true;
		} else if (modalType === "generate") {
			const totalValue = filteredRows
				.filter((r) => selectedIds.includes(r.Installment_id))
				.reduce((acc, cur) => {
					const amount = Number(cur.Installment_amount);
					return cur.Installment_type_amount === "discount"
						? acc - amount
						: acc + amount;
				}, 0);

			const payload: Omit<IGenerateInvoice, "id"> = {
				status: "pending",
				clientId: row.clientId,
				dueDate: dueDate + " 12:00:00",
				reference: generateReferenceFromPeriod(dataIni, dataFim),
				value: totalValue.toFixed(2),
				installmentsNumber: selectedIds.length,
				gateway: "EMPTY",
			};

			await handleGenerateInvoice(payload); // ✅ aqui está correto
			success = true;
		}


		dispatch({ type: "CLOSE_CONFIRM_MODAL" });
		if (success && onSuccess) {
			setTimeout(() => {
				onSuccess();
			}, 200);
		}
		onClose();
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
				<DialogTitle sx={{ color: "primary.main", fontWeight: 400, display: "flex", justifyContent: "space-between" }}>
					{title}
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					{isLoading ? (
						<Stack alignItems="center" py={4}>
							<CircularProgress />
							<Typography variant="body2" mt={2}>
								Carregando...
							</Typography>
						</Stack>
					) : (
						<Box sx={{ maxHeight: 500, overflowY: "auto" }}>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
								<TextField
									placeholder="Pesquisar por nome ou documento"
									variant="outlined"
									size="small"
									fullWidth
									value={searchText}
									onChange={(e) => setSearchText(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
										endAdornment: searchText && (
											<InputAdornment position="end">
												<IconButton size="small" onClick={() => setSearchText("")}>
													{" "}
													<ClearIcon fontSize="small" />{" "}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{ maxWidth: 400 }}
								/>
								<Typography variant="body2" sx={{ ml: 2 }}>
									{filteredRows.length} registro(s) encontrado(s)
								</Typography>
							</Stack>

							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell padding="checkbox">
											<Checkbox checked={isAllSelected} indeterminate={isIndeterminate} onChange={toggleSelectAll} />
										</TableCell>
										<TableCell>Nome</TableCell>
										<TableCell>Documento</TableCell>
										<TableCell>Tipo</TableCell>
										<TableCell>Parcela</TableCell>
										<TableCell>Valor</TableCell>
										<TableCell>Vencimento</TableCell>
										<TableCell>Forma</TableCell>
										<TableCell>Status</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredRows.map((item) => {
										const isSelectable = item.Installment_status === "pending";
										return (
											<TableRow key={item.Installment_id} selected={selectedIds.includes(item.Installment_id)} hover>
												<TableCell padding="checkbox">
													<Tooltip
														title={!isSelectable ? "Só é possível selecionar cobranças faturadas." : ""}
														arrow
														disableHoverListener={isSelectable}
													>
														<span>
															<Checkbox
																checked={selectedIds.includes(item.Installment_id)}
																onChange={() => toggleSelectOne(item.Installment_id)}
																disabled={!isSelectable}
															/>
														</span>
													</Tooltip>
												</TableCell>
												<TableCell>{item.name}</TableCell>
												<TableCell>{globalMaskDocument(item.document)}</TableCell>
												<TableCell>{globalTranslatePayment(item.Installment_type_amount)}</TableCell>
												<TableCell>
													{item.Installment_type_amount === "parcel" ? `${item.Installment_number}/${item.installments_count}` : "-"}
												</TableCell>
												<TableCell>{globalFormatCurrencyBRL(item.Installment_amount)}</TableCell>
												<TableCell>{formatDateFnsRemoveTimezone(item.Installment_due_date)}</TableCell>
												<TableCell>{globalTranslateMethodPayment(item.Installment_payment_method)}</TableCell>
												<TableCell>
													<Chip
														label={globalTranslateStatusInvoices(item.Installment_status)}
														size="small"
														sx={{
															fontWeight: 500,
															backgroundColor: globalBackgroundPayment(item.Installment_status),
															color: globalColorText(item.Installment_status),
														}}
													/>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</Box>
					)}
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>{cancelLabel}</Button>
					<Button variant="contained" color="primary" disabled={selectedIds.length === 0} onClick={() => dispatch({ type: "OPEN_CONFIRM_MODAL" })}>
						{confirmLabel}
					</Button>
				</DialogActions>
			</Dialog>

			<ModalConfirmSelect
				open={openConfirmModal}
				onClose={() => dispatch({ type: "CLOSE_CONFIRM_MODAL" })}
				onConfirm={handleConfirmAction}
				title={confirmTitle}
				description={confirmDescription}
				confirmLabel={confirmLabel}
				cancelLabel={cancelLabel}
				customContent={renderCustomContent()}
				isLoading={loadingAction}
				disabledConfirmButton={["cancel", "manual"].includes(modalType) && obs.trim().length === 0}
			/>
		</>
	);
};
